import type { Edge } from '@xyflow/react';
import type {
  FlowNode,
  DecisionNextResult,
  NodeNextKeyMap,
  LayoutNode,
} from '../../types';
import { DECISION_RESULT_LABELS } from '../../types';

export interface NodesAndEdges {
  nodes: LayoutNode[];
  edges: Edge[];
}

// gets edges based on the next key mapping
export const computeEdgesForNodeType = (
  sourceId: string,
  nodeType: FlowNode['nodeType'],
  node: FlowNode,
  nextKeyMap: NodeNextKeyMap,
) => {
  const edges: Edge[] = [];
  const nextKey = nodeType !== 'end' ? nextKeyMap[nodeType] : undefined;

  if (!nextKey) return edges;

  const nextVal = node[nextKey];

  // Decision: when nextKey resolves with true/false branching
  // other node types have no label on the edge
  if (nodeType === 'decision') {
    const arr = Array.isArray(nextVal) ? nextVal : [];
    arr.forEach((entry) => {
      if (!entry) return;
      const target =
        typeof entry.nextNodeId === 'string' ? entry.nextNodeId : undefined;
      if (!target) return;
      const result: DecisionNextResult =
        entry.result === 'false' ? 'false' : 'true';
      edges.push({
        type: 'custom',
        source: sourceId,
        sourceHandle: `${sourceId}_${result}`,
        label: DECISION_RESULT_LABELS[result],
        target,
        id: `${sourceId}->${target}`,
      });
    });
    return edges;
  }

  // Action or other detail nodes: nextKey typically points to array of target ids
  const arr = Array.isArray(nextVal) ? nextVal : [];
  arr.forEach((targetId) => {
    if (!targetId) return; // skip invalid entry
    const edge: Edge = {
      type: 'custom',
      source: sourceId,
      target: targetId,
      id: `${sourceId}->${targetId}`,
    };
    edges.push(edge);
  });

  return edges;
};

export const getCalculatedNodesAndEdges = (
  flowNodes: FlowNode[],
  nodeNextKeyMap: NodeNextKeyMap,
  options?: { addStartNode?: boolean; addEndNode?: boolean },
): NodesAndEdges => {
  const nodes: LayoutNode[] = flowNodes.map(
    ({ nodeLabel, nodeId, nodeType, ...flowNode }: FlowNode) =>
      ({
        id: nodeId,
        type: nodeType,
        data: { ...flowNode, label: nodeLabel },
        position: { x: 0, y: 0 },
      } as LayoutNode),
  );
  const edges: Edge[] = [];

  flowNodes.forEach(({ nodeId: sourceId, nodeType, ...rest }) => {
    const computed = computeEdgesForNodeType(
      sourceId,
      nodeType,
      { nodeId: sourceId, nodeType, ...rest },
      nodeNextKeyMap,
    );
    edges.push(...computed);
  });

  // Optionally add start and/or end nodes if configured and if they don't already exist in the input.
  const shouldAddStart =
    options?.addStartNode === true && !nodes.some((n) => n.type === 'start');
  const shouldAddEnd =
    options?.addEndNode === true && !nodes.some((n) => n.type === 'end');

  if (flowNodes.length > 0 && (shouldAddStart || shouldAddEnd)) {
    // Build quick lookup sets for existing edge connections
    const existingEdgeIds = new Set(edges.map((e) => e.id));
    const targetsWithIncoming = new Set(edges.map((e) => e.target));
    const sourcesWithOutgoing = new Set(edges.map((e) => e.source));

    // Determine which nodes currently have no incoming edges (need start -> node)
    const nodesWithoutIncoming = nodes.filter(
      (n) => !targetsWithIncoming.has(n.id),
    );

    // Determine which nodes currently have no outgoing edges (need node -> end)
    const nodesWithoutOutgoing = nodes.filter(
      (n) => !sourcesWithOutgoing.has(n.id),
    );

    if (shouldAddStart && nodesWithoutIncoming.length > 0) {
      const virtualStartNode: LayoutNode = {
        id: '__start__',
        type: 'start',
        data: {},
        position: { x: 0, y: 0 },
      };
      nodes.unshift(virtualStartNode);

      nodesWithoutIncoming.forEach((n) => {
        const id = `${virtualStartNode.id}->${n.id}`;
        if (!existingEdgeIds.has(id)) {
          edges.unshift({
            id,
            type: 'custom',
            source: virtualStartNode!.id,
            target: n.id,
          });
          existingEdgeIds.add(id);
        }
      });
    }

    if (shouldAddEnd && nodesWithoutOutgoing.length > 0) {
      const virtualEndNode: LayoutNode = {
        id: '__end__',
        type: 'end',
        data: {},
        position: { x: 0, y: 0 },
      };
      nodes.push(virtualEndNode);
      nodesWithoutOutgoing.forEach((n) => {
        const id = `${n.id}->${virtualEndNode!.id}`;
        // Create end edges from every node without an outgoing edge if applicable
        if (!existingEdgeIds.has(id)) {
          edges.push({
            id,
            type: 'custom',
            // if the source node is a decision, label the end edge as TRUE
            label:
              n.type === 'decision' ? DECISION_RESULT_LABELS.true : undefined,
            source: n.id,
            target: virtualEndNode!.id,
          });
          existingEdgeIds.add(id);
        }
      });
    }
  }

  return { nodes, edges };
};
